import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Icon } from 'semantic-ui-react'
import Div100vh from 'react-div-100vh'
import SequenceRenderer from './sequence-renderer'
import Jukebox from './jukebox'
import ObjectModal from './object-modal'
import StoryStartScreen from './story-start-screen'
import StoryAboutScreen from './story-about-screen'
import StoryPlayerOptions from './story-player-options'
import StoryCredits from './story-credits'
import WinObjectsPanel from './win-objects-panel'
import SvgIcon from './svg-icon'
import { loadFont } from './load-google-font'
import * as themes from './themes'
import * as textEffects from './text-effects'
import './story-player.css'

const usePrevious = (value) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const StoryPlayer = (props) => {
  const [story, setStory] = useState(null)
  const [numSequencesTotal, setNumSequencesTotal] = useState(0)
  const [sequencesShown, setSequencesShown] = useState([])
  const [numHeroesTotal, setNumHeroesTotal] = useState(0)
  const [heroesShown, setHeroesShown] = useState([])
  const [numObjectsTotal, setNumObjectsTotal] = useState(0)
  const [objectsShown, setObjectsShown] = useState([])
  const [sequenceId, setSequenceId] = useState(null)
  const [gameData, setGameData] = useState(props.defaultGameData || {})
  const [modalObjData, setModalObjData] = useState(null)
  const [showStartScreen, setShowStartScreen] = useState(!props.isPreview)
  const [gameOptions, setGameOptions] = useState({})
  const [soundsLoaded, setSoundsLoaded] = useState(false)
  const [soundsProgress, setSoundsProgress] = useState(0)
  const [path, setPath] = useState([])
  const [showOptions, setShowOptions] = useState(false)
  const [numReplays, setNumReplays] = useState(0)
  const [numVictories, setNumVictories] = useState(0)
  const [numDefeats, setNumDefeats] = useState(0)
  const [numUndo, setNumUndo] = useState(0)
  const [savePoints, setSavePoints] = useState([])
  const [showCredits, setShowCredits] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [showObjects, setShowObjects] = useState(false)

  const jukeboxRef = useRef(null)

  useEffect(() => {
    if (!props.story) {
      throw new Error('No story was given!')
    }
    const th = props.story.theme || {}
    setHeroesShown([])
    setObjectsShown([])
    setSequencesShown([props.story.firstSequence])
    setSequenceId(props.story.firstSequence)
    setNumSequencesTotal(Object.keys(props.story.sequences).length)
    const allAssets = Object.entries(props.story.assets).map(([_, s]) => s)
    setNumHeroesTotal(allAssets.filter(({kind}) => kind === 'hero').map(({id}) => id).length)
    setNumObjectsTotal(allAssets.filter(({kind}) => kind === 'object').map(({id}) => id).length)
    const firstSeq = props.story.sequences ? props.story.sequences[props.story.firstSequence] : null
    if (firstSeq && firstSeq.choices && firstSeq.choices.length > 0) {
      setSavePoints([{
        id: props.story.firstSequence,
        path: [props.story.firstSequence],
        currentLoop: th.styles && th.styles.sfx ? th.styles.sfx.mainloop : null,
        gameData: {}
      }])
    } else {
      setSavePoints([])
    }
    setStory(props.story)
    setGameData(props.defaultGameData || {})
    if (th.styles && th.styles.fontFamily) {
      loadFont(th.styles.fontFamily)
    }
  }, [props.story])

  useEffect(() => {
    if (story && numReplays === 0) {
      updateSequence()
    }
  }, [numReplays])

  const getSequence = (id) => {
    return story && story.sequences ? story.sequences[id] : null
  }

  const previous = usePrevious({ sequenceId })
  useEffect(() => {
    if (previous && previous.sequenceId !== sequenceId) {
      const seq = getSequence(sequenceId)
      if (seq.final) {
        fadeLoop()
        if (seq.isHappyEnd && styles.sfx && styles.sfx.gameWin) {
          playSound(styles.sfx.gameWin)
        } else if (!seq.isHappyEnd && styles.sfx && styles.sfx.gameLose) {
          playSound(styles.sfx.gameLose)
        }
      }
      if (seq && seq.soundSfx) {
        playSound(seq.soundSfx)
      }
      if (seq && seq.soundLoop && !seq.final) {
        playSound(seq.soundLoop, true)
      }
    }
  }, [sequenceId, getSequence])

  const getAssetById = (id) => {
    return story.assets[id] || {}
  }

  const getIconById = (id) => {
    return getAssetById(id).icon
  }

  const executeAction = (id, action, condition) => {
    if (action) {
      const { params } = action
      const asset = getAssetById(params)
      switch(asset.kind) {
        case 'hero': {
          if (!heroesShown.includes(params)) {
            setHeroesShown([...heroesShown, params])
          }
          setModalObjData({asset, id, condition})
          setGameData({...gameData, hero: params})
          if (styles.sfx && styles.sfx.objectWin) {
            playSound(styles.sfx.objectWin)
          }
          break
        }
        case 'object': {
          if (!objectsShown.includes(params)) {
            setObjectsShown([...objectsShown, params])
          }
          if (gameData.objects && gameData.objects.includes(params)) {
            setGameData({...gameData, objects: gameData.objects.filter(x => x !== params)})
            updateSequence(id, condition)
          } else {
            setModalObjData({asset, id, condition})
            const objects = [...(gameData.objects || []), params]
            setGameData({...gameData, objects})
            if (styles.sfx && styles.sfx.objectWin) {
              playSound(styles.sfx.objectWin)
            }
          }
          break
        }
        default: {
          updateSequence(id, condition)
          throw new Error(`The action '${asset.kind}' is unknown`)
        }
      }
    }
  }

  const gotoSequence = (id, action, condition) => {
    if (action) {
      executeAction(id, action, condition)
    } else {
      updateSequence(id, condition)
    }
  }

  const updateSequence = (id, condition) => {
    if (!id) {
      setNumReplays(numReplays + 1)
      if (sequencesShown.length === 0) {
        setSequencesShown([story.firstSequence])
      }
      if (story.sequences[story.firstSequence] && story.sequences[story.firstSequence].choices && story.sequences[story.firstSequence].choices.length > 0) {
        setSavePoints([{
          id: story.firstSequence,
          path: [story.firstSequence],
          currentLoop: jukeboxRef.current ? jukeboxRef.current.getCurrentLoop() : null,
          gameData
        }])
      } else {
        setSavePoints([])
      }
      setPath([story.firstSequence])
      setGameData(props.defaultGameData || {})
      setSequenceId(story.firstSequence)
      stopAllSounds()
      startStoryFirstSound(gameOptions)
      return
    }
    setPath([...path, id])
    if (condition && condition.next) {
      let respectCondition = false
      const { kind } = getAssetById(condition.params)
      if (kind === 'hero' && gameData.hero === condition.params) {
        respectCondition = true
      }
      if (kind === 'object' && gameData.objects && gameData.objects.includes(condition.params)) {
        respectCondition = true
      }
      if (respectCondition) {
        if (!sequencesShown.includes(condition.next)) {
          setSequencesShown([...sequencesShown, condition.next])
        }
        setSequenceId(condition.next)
        return
      }
    }
    if (!sequencesShown.includes(id)) {
      setSequencesShown([...sequencesShown, id])
    }
    setSequenceId(id)
    if (story.sequences[id] && story.sequences[id].final) {
      if (story.sequences[id].isHappyEnd) {
        setNumVictories(numVictories + 1)
      } else {
        setNumDefeats(numDefeats + 1)
      }
    }
    if (story.sequences[id] && story.sequences[id].choices && story.sequences[id].choices.length > 0) {
      const newSavePoints = [...savePoints, {
        id,
        path: [...path, id],
        gameData,
        currentLoop: jukeboxRef.current ? jukeboxRef.current.getCurrentLoop() : null
      }]
      setSavePoints(newSavePoints)
    }
  }

  const undo = () => {
    let undoPoint = savePoints[savePoints.length - 1]
    if (sequenceId === undoPoint.id) {
      setSavePoints(savePoints.slice(0, -1))
      undoPoint = savePoints[savePoints.length - 2]
    }
    stopAllSounds()
    const {id, path, gameData, currentLoop} = undoPoint
    setGameData(gameData)
    setPath(path)
    setNumUndo(numUndo + 1)
    if (currentLoop) {
      playSound(currentLoop, true)
    }
    setSequenceId(id)

  }

  const modalClosed = () => {
    const {id, condition} = modalObjData
    setModalObjData(null)
    updateSequence(id, condition)
  }

  const getStyles = (story, theme) => {
    const storyStyles = (story && story.theme && story.theme.styles) ? story.theme.styles : {}
    return {...theme.styles, ...storyStyles}
  }

  const themeName = story && story.theme && story.theme.name
  const theme = themes[themeName] || {}
  const styles = getStyles(story, {...theme.standard, ...theme})

  const containerStyles = {
    background: styles.background,
    fontFamily : styles.fontFamily,
    ...(props.styles || {})
  }

  const playSound = (soundRef, isLoop=false) => {
    if (jukeboxRef.current && gameOptions.sounds) {
      jukeboxRef.current.playSound(soundRef, isLoop)
    }
  }

  const stopAllSounds = () => {
    if (jukeboxRef.current && gameOptions.sounds) {
      jukeboxRef.current.stopAllSounds()
    }
  }

  const fadeLoop = () => {
    if (jukeboxRef.current && gameOptions.sounds) {
      jukeboxRef.current.fadeLoop()
    }
  }

  const onPlayClickSound = () => {
    if (styles.sfx && styles.sfx.button) {
      playSound(styles.sfx.button)
    }
  }

  const startStoryFirstSound = (options) => {
    stopAllSounds()
    if (options.sounds && !props.isPreview && jukeboxRef.current) {
      const firstSeq = props.story.sequences ? props.story.sequences[props.story.firstSequence] : null
      if (firstSeq && firstSeq.soundLoop && firstSeq.soundLoop.sound) {
        jukeboxRef.current.playSound(firstSeq.soundLoop, true)
      } else if (styles.sfx && styles.sfx.mainloop) {
        jukeboxRef.current.playSound(styles.sfx.mainloop, true)
      }
    }
  }

  const startStory = (options) => {
    setShowStartScreen(false)
    setGameOptions(options)
    setNumReplays(1)
    startStoryFirstSound(options)
  }

  const reset = () => {
    setNumUndo(0)
    setNumVictories(0)
    setNumDefeats(0)
    setHeroesShown([])
    setObjectsShown([])
    setSequencesShown([story.firstSequence])
    setNumReplays(0)
  }

  const restart = () => {
    hideOptionsPanel(false, true)
    updateSequence()
  }

  const showAboutPanel = () => {
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause()
    }
    setShowAbout(true)
  }

  const hideAboutPanel = () => {
    setShowAbout(false)
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause()
    }
  }

  const showOptionsPanel = () => {
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause()
    }
    setShowOptions(true)
  }

  const hideOptionsPanel = (resetGame=false, resetAllSounds=false) => {
    setShowOptions(false)
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause(resetAllSounds)
    }
    if (resetGame) {
      reset()
    }
  }

  const showObjectsPanel = () => {
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause()
    }
    setShowObjects(true)
  }

  const hideObjectsPanel = () => {
    setShowObjects(false)
    if (jukeboxRef.current) {
      jukeboxRef.current.togglePause()
    }
  }
  
  const textStyle = textEffects.getTextStyle(styles.textStyle)

  const computedStyles = {
    textStyle,
    sequence: {
      background: styles.sequenceBackground,
      color: styles.sequenceText,
      fontFamily: styles.fontFamily
    },
    button: {
      background: styles.buttonBackground,
      color: styles.buttonText,
      fontFamily: styles.fontFamily,
      ...textStyle
    }
  }

  return story ? (
    <Fragment>
      <div className="story-player-container" style={containerStyles}>
        <Div100vh className={ showStartScreen || showOptions || showAbout || showObjects ? 'blurred' : '' }>
          <div className="topbar" style={{background: styles.topbarBackground, color: styles.topbarText}}>
            MOIKI - { story.meta ? story.meta.name : `L'aventure qu'on choisit` }
          </div>
          <div className="main-content">
            { (gameData.hero || (gameData.objects && gameData.objects.length > 0)) && (
              <div className="game-bar" style={{ background: styles.assetsBackground }}>
                {gameData.hero ? (
                  <SvgIcon
                    icon={getIconById(gameData.hero)}
                    className="hero"
                    style={{ fill: styles.assetsFill, stroke: styles.assetsFill }}
                  />
                ) : (<div />)}
                <div className="game-objects">
                  {gameData.objects && gameData.objects.length > 0 && (
                    <button
                      style={ computedStyles.button }
                      onClick={showObjectsPanel}
                    ><Icon name='star' style={{ margin: 0 }} /> ({gameData.objects.length})</button>
                  )}
                </div>
              </div>
            )}
            { !modalObjData && (
              <SequenceRenderer
                styles={computedStyles}
                playClickSound={onPlayClickSound}
                sequence={ getSequence(sequenceId) }
                gotoSequence={gotoSequence}
                theme={{styles}}
              />
            )}
          </div>
          { props.showMenu && (
            <Fragment>
              <div
                style={{
                  position: 'absolute',
                  bottom: 10,
                  left: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column'
                }}
                className="player-menu"
              >
                { savePoints.length > 0 && (savePoints[savePoints.length - 1].id !== sequenceId || savePoints.length > 1) && (
                  <button
                    disabled={ sequenceId === story.firstSequence}
                    style={ computedStyles.button }
                    onClick={undo}
                  ><Icon name='undo' style={{ margin: 0 }} /></button>
               )}
                <button
                  style={ computedStyles.button }
                  onClick={showOptionsPanel}
                ><Icon name='setting' style={{ margin: 0 }} /></button>
                <button
                  style={ computedStyles.button }
                  onClick={showAboutPanel}
                ><Icon name='info' style={{ margin: 0 }} /></button>
              </div>
              <div style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                padding: '0.2em 0.5em',
                borderRadius: '1em',
                opacity: .7,
                ...computedStyles.sequence
              }}>{sequencesShown.length} / {numSequencesTotal}</div>
            </Fragment>
          )}
        </Div100vh>
      </div>
      { modalObjData && (
        <ObjectModal
          textStyle={textStyle}
          params={modalObjData}
          theme={{styles}}
          onClosed={modalClosed}
        />
      )}
      { story.sounds && (
        <Jukebox
          ref={jukeboxRef}
          sounds={story.sounds}
          onReady={() => setSoundsLoaded(true)}
          onProgress={(percent) => setSoundsProgress(percent)}
        />
      )}
      { showStartScreen && !showCredits && (
        <div className='story-overlay'>
          <StoryStartScreen
            story={story}
            soundsLoaded={soundsLoaded}
            soundsProgress={soundsProgress}
            onStart={startStory}
            onShowCredits={() => setShowCredits(true)}
            onClose={() => props.navigateTo('/stories')}
          />
        </div>
      )}
      { showOptions && !showCredits && (
        <div className='story-overlay'>
          <StoryPlayerOptions
            story={story}
            onClose={(options) => hideOptionsPanel(false)}
            onShowCredits={() => setShowCredits(true)}
            onReset={(options) => hideOptionsPanel(true, true)}
            onRestart={restart}
            gameOptions={gameOptions}
            stats={{
              numSequencesShown: sequencesShown.length,
              numSequencesTotal,
              numHeroesShown: heroesShown.length,
              numHeroesTotal,
              numObjectsShown: objectsShown.length,
              numObjectsTotal,
              numUndo,
              numReplays,
              numVictories,
              numDefeats
            }}
          />
        </div>
      )}
      { showAbout && !showCredits && (
        <div className='story-overlay'>
          <StoryAboutScreen
            story={story}
            onClose={hideAboutPanel}
          />
        </div>
      )}
      { showCredits && (
        <div className='story-overlay'>
          <StoryCredits
            story={story}
            onClose={() => setShowCredits(false)}
          />
        </div>
      )}
      { showObjects && (
        <div className='story-overlay'>
          <WinObjectsPanel
            getAssetById={getAssetById}
            objects={gameData.objects}
            onClose={hideObjectsPanel}
            styles={styles}
          />
        </div>
      )}
    </Fragment>
  ) : null
}

export default StoryPlayer
